/**
 * Componente: Formulario de Personal (alta/edición)
 * Alta: nombre, email y rol nada más — sin contraseña, se manda una
 * invitación por mail para que la persona elija la suya. Edición: solo
 * nombre y email (el rol no se cambia desde acá).
 */

import React, { useState } from 'react';
import axios from 'axios';
import ConfirmarDescartarCambiosModal from './ConfirmarDescartarCambiosModal';
import { IconCerrar, IconAdvertencia } from './icons';

const API_URL = process.env.REACT_APP_API_URL;

const PersonalFormModal = ({ personaInicial, onClose, onSaved }) => {
  const esEdicion = !!personaInicial;
  const [nombreCompleto, setNombreCompleto] = useState(personaInicial?.nombre_completo || '');
  const [email, setEmail] = useState(personaInicial?.email || '');
  const [rol, setRol] = useState(personaInicial?.rol || 'operador');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);
  // Foto del estado inicial para saber si hay datos sin guardar al cerrar.
  const [inicial] = useState({
    nombreCompleto: personaInicial?.nombre_completo || '',
    email: personaInicial?.email || '',
    rol: personaInicial?.rol || 'operador',
  });
  const hayCambiosSinGuardar =
    nombreCompleto !== inicial.nombreCompleto || email !== inicial.email || rol !== inicial.rol;

  // El fondo oscuro ya no cierra la ventana: solo X o Cancelar, y con datos
  // modificados se pide confirmación antes de descartarlos.
  const pedirCerrar = () => {
    if (guardando) return;
    if (hayCambiosSinGuardar) {
      setConfirmarDescarte(true);
    } else {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!nombreCompleto.trim() || !email.trim()) {
      setError('Completá nombre y email');
      return;
    }

    try {
      setGuardando(true);
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (esEdicion) {
        await axios.put(
          `${API_URL}/personal/${personaInicial.id}`,
          { nombre_completo: nombreCompleto.trim(), email: email.trim() },
          { headers }
        );
        onSaved();
      } else {
        await axios.post(
          `${API_URL}/personal`,
          { nombre_completo: nombreCompleto.trim(), email: email.trim(), rol },
          { headers }
        );
        onSaved(`Se envió un mail de invitación a ${email.trim()}`);
      }
    } catch (err) {
      console.error('Error al guardar:', err);
      setError(err.response?.data?.mensaje || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{esEdicion ? 'Editar' : 'Nuevo'} integrante del personal</h2>
          <button type="button" className="modal-close" onClick={pedirCerrar}><IconCerrar size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            {error && (
              <div className="alert alert-error">
                <IconAdvertencia size={18} />
                <p>{error}</p>
              </div>
            )}

            {!esEdicion && (
              <p className="form-hint">
                Se crea la cuenta sin contraseña — la persona la elige ella misma desde un link que le llega por mail. Nadie más la conoce, ni vos.
              </p>
            )}

            <div className="form-field">
              <label>Nombre completo *</label>
              <input
                className="padron-input"
                value={nombreCompleto}
                onChange={(e) => setNombreCompleto(e.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <label>Email *</label>
              <input
                type="email"
                className="padron-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <label>Rol {esEdicion && '(no se puede cambiar desde acá)'}</label>
              <select
                className="padron-input"
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                disabled={esEdicion}
              >
                <option value="admin">Administrador</option>
                <option value="operador">Operador</option>
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancelar" onClick={pedirCerrar} disabled={guardando}>
              Cancelar
            </button>
            <button type="submit" className="btn-guardar" disabled={guardando}>
              {guardando ? 'Guardando...' : esEdicion ? 'Guardar cambios' : 'Crear e invitar'}
            </button>
          </div>
        </form>
      </div>
    </div>
    {confirmarDescarte && (
      <ConfirmarDescartarCambiosModal
        onSeguirEditando={() => setConfirmarDescarte(false)}
        onDescartar={() => { setConfirmarDescarte(false); onClose(); }}
      />
    )}
    </>
  );
};

export default PersonalFormModal;
